import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Grid2,
  FormControl,
  Checkbox,
  Link,
  Box,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormControlLabel
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import CakeIcon from '@mui/icons-material/Cake';

import { ErrorAlert } from './Alerts';
import liffService from '../services/liff';
import { scrollToFirstError } from '../utils/formValidation';
import { AddressFields } from '../types';
import AddressInput from './AddressInput';
import { formatPhoneNumber, stripPhoneHyphens } from '../utils/phoneFormat';
import { calcAge, MIN_AGE } from '../utils/age';
import { CURRENT_TERMS_VERSION, WORKER_TERMS_URL, PRIVACY_POLICY_URL } from '../constants/terms';

export interface UserRegistrationData {
  email: string;
  last_name: string;
  first_name: string;
  birth_date: string;
  phone: string;
  address: string;
  postal_code?: string;
  prefecture?: string;
  city?: string;
  street?: string;
  building?: string;
  /** 利用規約（即給条項含む）とプライバシーポリシーへの同意（必須） */
  terms_agreed: boolean;
  /** 同意した利用規約のバージョン（省略時はBackend既定の "1.0"） */
  terms_agreed_version?: string;
}

// 利用規約バージョンは共通定数（src/constants/terms.ts）へ集約。後方互換のため再エクスポートする。
export { CURRENT_TERMS_VERSION } from '../constants/terms';

interface UserRegistrationPopupProps {
  open: boolean;
  onClose: () => void;
  // 登録の成否を返す。失敗理由はこのダイアログ内に表示する（StoreRegistrationDialog /
  // AccountReactivationDialog と同じ約束）。await するので送信中は isSubmitting で
  // ボタンを塞げる＝二重送信（ユーザー二重作成）を防げる。
  onComplete: (userData: UserRegistrationData) => Promise<{ success: boolean; error?: string }>;
}

const UserRegistrationPopup: React.FC<UserRegistrationPopupProps> = ({
  open,
  onClose,
  onComplete
}) => {
  const [formData, setFormData] = useState<UserRegistrationData>({
    email: '',
    last_name: '',
    first_name: '',
    birth_date: '',
    phone: '',
    address: '',
    postal_code: '',
    prefecture: '',
    city: '',
    street: '',
    building: '',
    terms_agreed: false,
    terms_agreed_version: CURRENT_TERMS_VERSION,
  });

  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');

  const [errors, setErrors] = useState<{
    last_name?: string;
    first_name?: string;
    email?: string;
    birth_date?: string;
    phone?: string;
    address?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 登録ボタンが一度でも押されたか（押下後は住所入力欄でも個別フィールドのエラーを表示）
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // 利用規約とプライバシーポリシーの同意
  const [agreed, setAgreed] = useState(false);
  const handleInputChange = (field: 'email' | 'birth_date' | 'phone' | 'address') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'phone' ? formatPhoneNumber(e.target.value) : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {
      last_name?: string;
      first_name?: string;
      email?: string;
      birth_date?: string;
      phone?: string;
      address?: string;
    } = {};

    // 必須フィールドのチェック
    if (!lastName.trim()) {
      newErrors.last_name = '姓を入力してください';
    }
    if (!firstName.trim()) {
      newErrors.first_name = '名を入力してください';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    if (!formData.birth_date) {
      newErrors.birth_date = '生年月日を入力してください';
    } else {
      // 16歳以上のチェック（満年齢は日まで比較する。詳細は utils/age.ts）
      const age = calcAge(formData.birth_date);

      if (age === null) {
        newErrors.birth_date = '生年月日を入力してください';
      } else if (age < MIN_AGE) {
        newErrors.birth_date = `${MIN_AGE}歳以上である必要があります`;
      }
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号を入力してください';
    } else {
      const digitsOnly = stripPhoneHyphens(formData.phone);
      if (digitsOnly.length < 10 || digitsOnly.length > 11) {
        newErrors.phone = '有効な電話番号を入力してください';
      }
    }

    if (!formData.prefecture?.trim() || !formData.city?.trim()) {
      newErrors.address = '住所を入力してください';
    }

    setErrors(newErrors);
    const errorKeys = Object.keys(newErrors) as string[];
    if (errorKeys.length > 0) {
      const fieldIdMap: Partial<Record<string, string>> = {
        last_name: 'last-name-input',
        first_name: 'first-name-input',
        email: 'email-input',
        birth_date: 'birth-date-input',
        phone: 'phone-input',
        address: 'address-input',
      };
      scrollToFirstError(errorKeys.map(k => fieldIdMap[k]).filter((v): v is string => Boolean(v)));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // 親コンポーネントに登録データを渡す（姓・名を別フィールドで送信）
      // agreed=true は UI 側で disabled 制御済み。同意日時はBackend側で記録する。
      // await しないと送信中もボタンが押せてしまい、ユーザーが二重作成される。
      const result = await onComplete({
        ...formData,
        last_name: lastName.trim(),
        first_name: firstName.trim(),
        terms_agreed: agreed,
        terms_agreed_version: CURRENT_TERMS_VERSION,
      });

      if (!result.success) {
        // 「このメールアドレスは既に登録されています」等の理由をこのダイアログ内に表示する。
        // 以前は AuthContext が全画面「初期化エラー」に飛ばしていて入力もやり直しだった。
        setError(result.error || '登録に失敗しました。入力内容をご確認ください。');
      }
    } catch (error) {
      setError('登録処理中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      // フォームをリセット
      setFormData({
        email: '',
        last_name: '',
        first_name: '',
        birth_date: '',
        phone: '',
        address: '',
        postal_code: '',
        prefecture: '',
        city: '',
        street: '',
        building: '',
        terms_agreed: false,
        terms_agreed_version: CURRENT_TERMS_VERSION,
      });
      setLastName('');
      setFirstName('');
      setErrors({});
      setError(null);
      setSubmitAttempted(false);
      setAgreed(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          ユーザー登録
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <ErrorAlert message={error} onClose={() => setError(null)} />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          サービスをご利用いただくには、以下の情報を入力してください。
        </Typography>

        <Grid2 container spacing={3}>
          <Grid2 size={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="last-name-input" shrink>姓 *</InputLabel>
              <OutlinedInput
                id="last-name-input"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value.replace(/[\s　]/g, ''));
                  if (errors.last_name) setErrors(prev => ({ ...prev, last_name: undefined }));
                }}
                label="姓 *"
                notched
                error={!!errors.last_name}
                disabled={isSubmitting}
              />
            </FormControl>
            {errors.last_name && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.last_name}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="first-name-input" shrink>名 *</InputLabel>
              <OutlinedInput
                id="first-name-input"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value.replace(/[\s　]/g, ''));
                  if (errors.first_name) setErrors(prev => ({ ...prev, first_name: undefined }));
                }}
                label="名 *"
                notched
                error={!!errors.first_name}
                disabled={isSubmitting}
              />
            </FormControl>
            {errors.first_name && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.first_name}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="email-input">メールアドレス *</InputLabel>
              <OutlinedInput
                id="email-input"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                startAdornment={
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                }
                label="メールアドレス *"
                error={!!errors.email}
                disabled={isSubmitting}
              />
            </FormControl>
            {errors.email && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.email}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="birth-date-input">生年月日 *</InputLabel>
              <OutlinedInput
                id="birth-date-input"
                type="date"
                value={formData.birth_date}
                onChange={handleInputChange('birth_date')}
                startAdornment={
                  <InputAdornment position="start">
                    <CakeIcon color="action" />
                  </InputAdornment>
                }
                label="生年月日 *"
                error={!!errors.birth_date}
                disabled={isSubmitting}
                inputProps={{
                  max: new Date().toISOString().split('T')[0] // 今日以前の日付のみ選択可能
                }}
              />
            </FormControl>
            {errors.birth_date && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.birth_date}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="phone-input">電話番号 *</InputLabel>
              <OutlinedInput
                id="phone-input"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                type="tel"
                startAdornment={
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                }
                label="電話番号 *"
                placeholder="例: 090-1234-5678"
                error={!!errors.phone}
                disabled={isSubmitting}
              />
            </FormControl>
            {errors.phone && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.phone}
              </Typography>
            )}
          </Grid2>

          <Grid2 size={12}>
            <AddressInput
              value={{
                postal_code: formData.postal_code || '',
                prefecture: formData.prefecture || '',
                city: formData.city || '',
                street: formData.street || '',
                building: formData.building || '',
              }}
              onChange={(addr: AddressFields) => {
                setFormData(prev => ({ ...prev, ...addr }));
                if (errors.address) {
                  setErrors(prev => ({ ...prev, address: undefined }));
                }
              }}
              required
              disabled={isSubmitting}
              size="small"
              showErrors={submitAttempted}
            />
            {errors.address && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {errors.address}
              </Typography>
            )}
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
             sx={{ fontSize: '0.5rem' }}
             control={
             <Checkbox
             checked={agreed}
             onChange={(e) => setAgreed(e.target.checked)}
             />
            }
            label={
              <Box component="span" sx={{fontSize: '0.85rem'}}>
                <Link href={WORKER_TERMS_URL} target="_blank" sx={{ mr: 0.5 }}>利用規約</Link>と<Link href={PRIVACY_POLICY_URL} target="_blank" sx={{ mx: 0.5 }}>プライバシーポリシー</Link>に同意します。
              </Box>}
            />
          </Grid2>
        </Grid2>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isSubmitting}
          variant="outlined"
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          // 送信中は必ず塞ぐ（onComplete を await するようになったため、ここを塞がないと
          // 連打でユーザーが二重作成される）
          disabled={!agreed || isSubmitting}
          variant="contained"
          sx={{ ml: 2 }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
              登録中...
            </>
          ) : (
            '登録する'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserRegistrationPopup;